import React, { useState, useContext, useMemo, useCallback } from "react";
import { AppContexts } from "../../../providers";
import { useHistory } from "react-router";
import { Icon, Image } from "uikit-react";
import Tags from "./Tags";
import DeleteRentalDialog from "./DeleteRentalDialog";
import QRPart from "../Qrcode";

// Shared table row for displaying rental entries with configurable columns and actions
const RentalTableRow = ({
  guide,
  index,
  allTags,
  config = {},
  onZoneToggle,
}) => {
  const {
    showGuideInfo = false,
    showPmsId = false,
    showEditButton = true,
    showViewButton = false,
    showPrintButton = false,
    showShareButton = false,
    showMonitoringButton = false,
    showQRButton = false,
    showCloneButton = true,
    showDetailButton = false,
    editPath = "properties/{id}",
    viewUrl = "https://guide.ruebarue.com/rental/{id}",
    printUrl = "https://guide.ruebarue.com/rental/{id}/print",
    shareModalId = "#share-modal",
    monitoringModalId = "#smart-lock-monitoring-modal",
    deleteModalId = "#modal-property-delete",
    onDetailClick = null,
  } = config;

  const DestinationContext = useContext(AppContexts.DestinationContext);
  const authContext = useContext(AppContexts.AuthContext);
  const userType = authContext.user?.details?.type;
  const {
    updateRental,
    setRental,
    selectedFlags,
    setSelectedFlags,
    setIsNew,
    createRental,
  } = useContext(AppContexts.RentalsContext);

  const { zoneGroups, toggleZoneItem, getZoneGroups } = useContext(
    AppContexts.ZonesContext,
  );

  const history = useHistory();
  const [guideDestination, setGuideDestination] = useState(null);
  const [pendingZones, setPendingZones] = useState({});
  const [cloneLoading, setCloneLoading] = useState(false);

  const listDestinations = useMemo(() => {
    if (!DestinationContext?.listDestinations) {
      return [];
    }
    return DestinationContext.listDestinations;
  }, [DestinationContext?.listDestinations]);

  const zoneRentals = useMemo(() => {
    return (zoneGroups?.rentals || []).reduce((a, c) => {
      a[c.id] = (c.rental_ids || []).map((id) => +id);

      return a;
    }, {});
  }, [zoneGroups]);

  const handleSelectedTags = async (selectedTags) => {
    let updated = { ...guide, tags: selectedTags };
    if (updated.hasOwnProperty("custom_macro_values"))
      delete updated["custom_macro_values"];
    try {
      await updateRental(updated.rental_id, updated);
    } catch (e) {
      console.log(e);
    }
  };

  const handleSelect = useCallback((event, index) => {
    setSelectedFlags((flags) => [
      ...flags.slice(0, index),
      event.target.checked,
      ...flags.slice(index + 1),
    ]);
  }, []);

  const handleClone = async (objRental) => {
    if (cloneLoading) {
      return;
    }

    setCloneLoading(true);

    const {
      rental_id,
      created_at,
      updated_at,
      attachments,
      ...objCloneRental
    } = objRental;

    try {
      objCloneRental.name += " - Cloned";
      objCloneRental.pms_id += " - Cloned";
      objCloneRental.clone_source_rental_id = rental_id;
      let createdRental = await createRental(objCloneRental);
      if (createdRental) {
        await getZoneGroups(["rentals"]);
      }
    } finally {
      setCloneLoading(false);
    }
  };

  const handleEdit = () => {
    setIsNew(false);
    setRental(guide);
  };

  const handleDetail = () => {
    if (onDetailClick) {
      onDetailClick(guide);
    } else {
      setRental(guide);
      history.push(`/unit_detail/${guide.rental_id}`);
    }
  };

  const interpolatePath = (path) => {
    return path.replace("{id}", guide.rental_id);
  };

  const toggleZone = async (zone, rental) => {
    if ((pendingZones[zone.id] || []).includes(rental.rental_id)) {
      return;
    }

    setPendingZones({
      ...pendingZones,
      [zone.id]: [...(pendingZones[zone.id] || []), rental.rental_id],
    });

    let response = await toggleZoneItem(zone, "rental", rental.rental_id);

    console.log("Response", response);
    onZoneToggle(response);

    setPendingZones({
      ...pendingZones,
      [zone.id]: [
        ...(pendingZones[zone.id] || []).filter(
          (id) => id !== rental.rental_id,
        ),
      ],
    });
  };

  return (
    <>
      <tr>
        <td>
          <div className="autosend-checkbox">
            <label htmlFor={`propertyCB${index}`}>
              <input
                type="checkbox"
                id={`propertyCB${index}`}
                checked={selectedFlags[index] || false}
                onChange={(event) => handleSelect(event, index)}
              />
            </label>
          </div>
        </td>

        <td>
          {showGuideInfo ? (
            <>
              <div>
                <div className="semi-bold-font">
                  <label className="uk-margin-small-right">
                    {guide.name && guide.name.trim() !== ""
                      ? guide.name
                      : "<No Guide Name>"}
                  </label>

                  {showEditButton && (
                    <span
                      className="edit-guide"
                      uk-toggle={`target: #addPropertyGuide-modal`}
                      uk-tooltip="Edit Property Details"
                      onClick={handleEdit}
                    >
                      <Image src="/images/settings.svg" />
                    </span>
                  )}
                </div>
              </div>
              <div className="uk-margin-very-small-top">
                <span className="secondary property-guide-label">Address</span>
                &nbsp;
                <span>{guide.address}</span>
              </div>
              <div>
                <span className="secondary property-guide-label">
                  Property ID
                </span>
                &nbsp;
                <span>
                  {guide.pms_id === "" ? "Click to Add an ID" : guide.pms_id}
                </span>
              </div>
              <div>
                <span className="secondary property-guide-label">
                  Area Guide
                </span>
                &nbsp;
                <div
                  uk-form-custom="target: > * > span:first-child"
                  className="uk-margin-small-right"
                >
                  <span>
                    {!!listDestinations.find(
                      (item) => item.destination_id === guide.destination_id,
                    )
                      ? listDestinations.find(
                          (item) =>
                            item.destination_id === guide.destination_id,
                        ).name
                      : "No area guide attached"}
                  </span>
                </div>
                {guideDestination === index && (
                  <span onClick={() => setGuideDestination(null)}>
                    <Icon options="close" button={true} />
                  </span>
                )}
              </div>
            </>
          ) : (
            <>
              <div>
                <div className="semi-bold-font">
                  {showPmsId
                    ? guide.pms_id === ""
                      ? "Click to Add an ID"
                      : guide.pms_id
                    : guide.name}
                </div>
              </div>
              <div className="secondary">
                {guide.address}
              </div>
            </>
          )}
        </td>

        <td className="hms-add-tag">
          <Tags
            value={guide.tags}
            all={allTags}
            onChange={handleSelectedTags}
          />
        </td>

        {!+authContext.user?.user?.owner_id && (
          <td className="hms-add-tag">
            <div className="uk-flex uk-flex-top">
              <div className="hms-add-tag">
                <button
                  className="uk-button"
                  type="button"
                  aria-haspopup="true"
                  aria-expanded="false"
                >
                  <img src="/images/group.svg" />
                </button>
                <div
                  data-uk-dropdown="mode: click"
                  className="guest-tags-dropdown uk-dropdown"
                >
                  <ul className="uk-nav uk-dropdown-nav tags-selector">
                    {(zoneGroups?.rentals || []).map((zone) => {
                      return (
                        <li>
                          <label>
                            <input
                              className="uk-checkbox"
                              type="checkbox"
                              value={zone.id}
                              disabled={(pendingZones[zone.id] || []).includes(
                                guide.rental_id,
                              )}
                              checked={zoneRentals[zone.id].includes(
                                +guide.rental_id,
                              )}
                              onChange={() => toggleZone(zone, guide)}
                            />
                            {zone.label}
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
              <div className="hms-guest-tags">
                <div className="guest-tags">
                  {(zoneGroups?.rentals || []).map((zone) => {
                    return (
                      (zone.rental_ids || []).includes(+guide.rental_id) && (
                        <div className="uk-inline zone">{zone.label}</div>
                      )
                    );
                  })}
                </div>
              </div>
            </div>
          </td>
        )}

        <td className="hms-action-btn">
          {showEditButton && (
            <div className="uk-inline">
              <button
                className="uk-button"
                type="button"
                onClick={() => {
                  if (editPath && editPath.includes("{id}")) {
                    // Navigate to edit page
                    history.push(interpolatePath(editPath));
                  } else {
                    // Open modal
                    handleEdit();
                  }
                }}
                uk-toggle="target: #addPropertyGuide-modal"
                uk-tooltip="Edit Home Guide"
              >
                <img src="/images/edit.svg" />
              </button>
            </div>
          )}

          {showViewButton && (
            <div className="uk-inline">
              <a
                className="uk-button"
                type="button"
                href={interpolatePath(viewUrl)}
                target="_blank"
                rel="noopener noreferrer"
                uk-tooltip="View Home Guide"
              >
                <img src="/images/guide.svg" />
              </a>
            </div>
          )}

          {showQRButton && (
            <div className="uk-inline">
              <QRPart
                idValue={guide.rental_id}
                qrUrl={`https://guide.ruebarue.com/rental/${guide.rental_id}`}
                iconSrc="/images/qr-code.svg"
                tooltip="Download QR Code"
              />
            </div>
          )}

          {showShareButton && (
            <div className="uk-inline">
              <button
                className="uk-button"
                type="button"
                onClick={() => setRental(guide)}
                uk-toggle={`target: ${shareModalId}`}
                uk-tooltip="Share"
              >
                <img src="/images/share.svg" />
              </button>
            </div>
          )}

          {showMonitoringButton && (
            <div className="uk-inline">
              <button
                className="uk-button"
                type="button"
                onClick={() => setRental(guide)}
                uk-toggle={`target: ${monitoringModalId}`}
                uk-tooltip="Smart Lock Monitoring"
              >
                <img src="/images/smart-lock-monitor.svg" alt="Smart Lock Monitoring" />
              </button>
            </div>
          )}

          {showCloneButton && userType !== "trial" && (
            <div className="uk-inline">
              <button
                className="uk-button"
                type="button"
                disabled={userType === "trial" || cloneLoading}
                onClick={() => handleClone(guide)}
                uk-tooltip={cloneLoading ? "Cloning..." : "Clone"}
              >
                {cloneLoading ? (
                  <span data-uk-spinner="ratio: 0.5;" />
                ) : (
                  <Image src="/images/clone.svg" />
                )}
              </button>
            </div>
          )}

          {showPrintButton && (
            <div className="uk-inline">
              <a
                className="uk-button"
                type="button"
                href={interpolatePath(printUrl)}
                target="_blank"
                rel="noopener noreferrer"
                uk-tooltip="Print"
              >
                <img src="/images/print.svg" />
              </a>
            </div>
          )}

          {showDetailButton && (
            <div className="uk-inline">
              <button
                className="uk-button"
                uk-toggle="target: #modal-guest-detail"
                onClick={handleDetail}
                uk-tooltip="Detail"
              >
                <Image src="/images/detail.svg" />
              </button>
            </div>
          )}

          <div className="uk-inline">
            <button
              className="uk-button"
              type="button"
              uk-toggle={`target: ${deleteModalId}`}
              onClick={() => setRental(guide)}
              uk-tooltip="Delete"
            >
              <img src="/images/delete.svg" />
            </button>
          </div>
        </td>
      </tr>
      <DeleteRentalDialog config={{ title: "Rental" }} />
    </>
  );
};

export default RentalTableRow;
